import { h, Host, Fragment, } from "@stencil/core";
import { searchParam } from "@eventstore-ui/router";
import { theme } from "@eventstore-ui/theme";
/**
 * A tabbed panel. Each panel can be targeted via a slot.
 * @slot [tabName] - Slots are created based off of the names of the passed tabs.
 * @part [tabName] - The tab and its corresponding panel.
 * @slot header-end - After all the tabs.
 * @part indicator - The sliding indicatior bar.
 * @part panel - Tab panels.
 * @part tab - Tabs.
 * @part active - The active tab.
 * @part tablist - The tab container.
 * @part inter-tab-icon - Icon between tabs (if specified).
 */
export class EsTabs {
    constructor() {
        this.observer = new ResizeObserver(() => {
            this.positionIndicator(this.active);
        });
        this.renderTab = ({ title, id, badge }, i) => (h(Fragment, null, i !== 0 && !!this.interTabIcon && (h("es-icon", { icon: this.interTabIcon, part: 'inter-tab-icon', size: this.interTabIconSize })), h("button", { "data-skip-focus-delegation": true, type: 'button', role: 'tab', "aria-controls": id, "aria-selected": this.active === id, id: `tab-${id}`, class: {
                tab: true,
                active: this.active === id,
                drag_over: this.activeDragOver === id,
            }, part: `tab ${id} ${this.active === id ? 'active' : ''}`, onClick: this.setActive(id), ref: this.captureTab(id), onDragEnter: this.dragEnterTab(id), onDragLeave: this.dragLeaveTab(id), onDrop: this.dropTab(id), onDragOver: this.dragOverTab }, h("es-badge", { count: badge?.() ? 1 : 0, variant: 'dot' }, h("span", { class: 'elipsis' }, title)))));
        this.setActive = (id) => () => {
            this.active = id;
        };
        this.getActive = () => this.tabs.find(({ id }) => id === this.active);
        this.tabRefs = new Map();
        this.captureTab = (id) => (ref) => {
            if (ref) {
                this.tabRefs.set(id, ref);
                this.observer.observe(ref);
                if (!this.indicatorAttactedTo && id === this.active) {
                    this.positionIndicator(this.active);
                }
            }
            else {
                if (this.tabRefs.has(id)) {
                    this.observer.unobserve(this.tabRefs.get(id));
                }
                this.tabRefs.delete(id);
            }
        };
        this.captureIndicatior = (ref) => {
            this.indicator = ref;
            this.positionIndicator(this.active);
        };
        this.dragTimeouts = new Map();
        this.dragCounts = new Map();
        this.dragEnterTab = (id) => (e) => {
            e.preventDefault();
            const count = (this.dragCounts.get(id) ?? 0) + 1;
            this.dragCounts.set(id, count);
            if (this.dragTimeouts.has(id))
                return;
            this.activeDragOver = id;
            this.dragTimeouts.set(id, setTimeout(() => {
                this.setActive(id)();
            }, 1000));
        };
        this.finishDrag = (id) => {
            clearTimeout(this.dragTimeouts.get(id));
            this.dragTimeouts.delete(id);
            this.dragCounts.delete(id);
            if (this.activeDragOver === id) {
                this.activeDragOver = undefined;
            }
        };
        this.dragLeaveTab = (id) => (e) => {
            e.preventDefault();
            const count = (this.dragCounts.get(id) ?? 1) - 1;
            this.dragCounts.set(id, count);
            if (count <= 0) {
                this.finishDrag(id);
            }
        };
        this.dropTab = (id) => (e) => {
            e.preventDefault();
            this.finishDrag(id);
        };
        this.dragOverTab = (e) => {
            e.preventDefault();
        };
        this.tabs = undefined;
        this.activeParam = 'tab';
        this.active = undefined;
        this.interTabIcon = undefined;
        this.interTabIconSize = 20;
        this.activeDragOver = undefined;
    }
    componentWillLoad() {
        this.setUpActiveParam(this.activeParam);
        this.updateActiveIfNoMatchesTabs(this.tabs);
    }
    disconnectedCallback() {
        this.observer.disconnect();
        this.searchParam?.delete();
        cancelAnimationFrame(this.indicatorFrame);
        this.dragTimeouts.forEach((t) => clearTimeout(t));
    }
    setUpActiveParam(param) {
        if (!param) {
            this.searchParam?.delete();
            this.searchParam = undefined;
        }
        else {
            this.searchParam = searchParam(param);
            if (this.active && this.searchParam && !this.searchParam.value) {
                this.searchParam.set(this.active);
            }
        }
        this.active = this.searchParam?.value ?? this.active ?? this.tabs[0].id;
    }
    updateActive(active) {
        this.searchParam?.set(active);
        this.tabChange.emit(active);
    }
    updateActiveIfNoMatchesTabs(tabs) {
        if (tabs.find(({ id }) => id === this.active))
            return;
        this.active = this.tabs[0].id;
    }
    render() {
        return (h(Host, { key: '1e6d7f3e960ca93a53135068478665e79d3c8169', "high-contrast": theme.isHighContrast(), dark: theme.isDark() }, h("header", { key: '63fb23a8419486b49ff87b55157f99e181aa6376', role: 'tablist', part: 'tablist' }, this.tabs.map(this.renderTab), h("slot", { key: '905c74e4bec69418b47ced540e81879c93449fa4', name: 'header-end' })), h("div", { key: '449750ddaf34934348dc5f7b5ae471ed6d8053b7', "aria-labelledby": `tab-${this.active}`, class: {
                panel: true,
                [this.getActive()?.panelVariant ?? '']: true,
            }, role: 'tabpanel', part: `panel ${this.active}` }, h("div", { key: 'edb177102d130ff9cd70950eca18cf3d2de543bb', role: 'presentation', class: 'indicator', ref: this.captureIndicatior, part: 'indicator' }), h("slot", { key: this.active, name: this.active }))));
    }
    positionIndicator(active) {
        if (!active)
            return;
        const tab = this.tabRefs.get(active);
        const indicator = this.indicator;
        if (!indicator || !tab)
            return;
        if (!!this.indicatorAttactedTo && active !== this.indicatorAttactedTo) {
            indicator.style.setProperty('transition-property', 'width, transform');
            indicator.addEventListener('transitionend', () => {
                indicator.style.removeProperty('transition-property');
            }, { once: true });
        }
        cancelAnimationFrame(this.indicatorFrame);
        this.indicatorFrame = requestAnimationFrame(() => {
            const { width, paddingLeft, paddingRight } = getComputedStyle(tab);
            const left = tab.offsetLeft;
            const newWidth = `calc(${width} - ${paddingLeft} - ${paddingRight})`;
            const transform = `translateX(calc(${left}px + ${paddingLeft}))`;
            indicator.style.width = newWidth;
            indicator.style.transform = transform;
            this.indicatorAttactedTo = active;
        });
    }
    static get is() { return "es-tabs"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["es-tabs.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["es-tabs.css"]
        };
    }
    static get properties() {
        return {
            "tabs": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Tab[]",
                    "resolved": "Tab[]",
                    "references": {
                        "Tab": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/es-tabs/types.ts::Tab"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A list of tabs."
                }
            },
            "activeParam": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "string | false",
                    "resolved": "boolean | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Reflect the active tab to a search param of name. Set to false to disable."
                },
                "attribute": "active-param",
                "reflect": false,
                "defaultValue": "'tab'"
            },
            "active": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The currently active panel. By default it will take from the passed activeParam, or the first tab."
                },
                "attribute": "active",
                "reflect": false
            },
            "interTabIcon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IconDescription",
                    "resolved": "[namespace: string | symbol, name: string] | string | undefined",
                    "references": {
                        "IconDescription": {
                            "location": "import",
                            "path": "../es-icon/types",
                            "id": "src/components/es-icon/types.ts::IconDescription"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Icon to be rendered between each tab."
                },
                "attribute": "inter-tab-icon",
                "reflect": false
            },
            "interTabIconSize": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "thu size of the icon to be rendered between each tab."
                },
                "attribute": "inter-tab-icon-size",
                "reflect": false,
                "defaultValue": "20"
            }
        };
    }
    static get states() {
        return {
            "activeDragOver": {}
        };
    }
    static get events() {
        return [{
                "method": "tabChange",
                "name": "tabChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Triggered when the active tab is changed. `detail` is the newly active tab."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "activeParam",
                "methodName": "setUpActiveParam"
            }, {
                "propName": "active",
                "methodName": "updateActive"
            }, {
                "propName": "tabs",
                "methodName": "updateActiveIfNoMatchesTabs"
            }, {
                "propName": "active",
                "methodName": "positionIndicator"
            }];
    }
}
//# sourceMappingURL=es-tabs.js.map
